import { AlertTriangle, Plus, RotateCcw, Save, Send, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { hasMinRole } from '../auth/adminAuthorization'
import { discardCmsDraft, fetchCmsContent, fetchCmsVersions, formatDateTime, publishCmsContent, rollbackCmsContent, saveCmsDraft } from '../lib/cms'
import { validateFeatureSettingsDraft } from '../../../../packages/validation/cms'
import { EMPTY_FEATURE_SETTINGS_DATA } from '../../../../packages/types/cms'

export default function FeatureSettingsPage() {
  const auth = useAdminAuth()
  const canEdit = hasMinRole(auth.role, 'editor')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [production, setProduction] = useState(null)
  const [draft, setDraft] = useState(null)
  const [config, setConfig] = useState(EMPTY_FEATURE_SETTINGS_DATA)
  const [versions, setVersions] = useState([])
  const [newToggleKey, setNewToggleKey] = useState('')
  const savedSnapshotRef = useRef(JSON.stringify(EMPTY_FEATURE_SETTINGS_DATA))

  async function load() {
    setLoading(true); setError('')
    try {
      const { production: prod, draft: dr } = await fetchCmsContent('featureSettings')
      setProduction(prod); setDraft(dr)
      const initial = dr?.data ?? prod?.data ?? EMPTY_FEATURE_SETTINGS_DATA
      setConfig(initial)
      savedSnapshotRef.current = JSON.stringify(initial)
      setVersions(await fetchCmsVersions('featureSettings'))
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const dirty = JSON.stringify(config) !== savedSnapshotRef.current
  const validation = useMemo(() => validateFeatureSettingsDraft(config), [config])
  const changedFields = useMemo(() => {
    const base = production?.data || EMPTY_FEATURE_SETTINGS_DATA
    const changed = []
    if (JSON.stringify(base.maintenanceMode) !== JSON.stringify(config.maintenanceMode)) changed.push('점검 모드')
    if (JSON.stringify(base.operationalMessage) !== JSON.stringify(config.operationalMessage)) changed.push('운영 메시지')
    if (JSON.stringify(base.featureToggles) !== JSON.stringify(config.featureToggles)) changed.push('기능 토글')
    if (JSON.stringify(base.minVersion) !== JSON.stringify(config.minVersion)) changed.push('최소 버전')
    if (JSON.stringify(base.forceUpdate) !== JSON.stringify(config.forceUpdate)) changed.push('강제 업데이트')
    return changed
  }, [production, config])

  async function saveDraft() {
    setBusy(true); setMessage(''); setError('')
    try {
      await saveCmsDraft('featureSettings', config, { uid: auth.user.uid, baseVersion: production?.version ?? 0 })
      savedSnapshotRef.current = JSON.stringify(config)
      const { draft: fresh } = await fetchCmsContent('featureSettings')
      setDraft(fresh)
      setMessage('초안을 저장했습니다.')
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function discardDraft() {
    if (!window.confirm('저장된 초안을 버리고 배포된 내용으로 되돌릴까요?')) return
    setBusy(true); setMessage(''); setError('')
    try { await discardCmsDraft('featureSettings', auth.user.uid); await load(); setMessage('초안을 버렸습니다.') } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function publish() {
    if (!validation.valid) { setError('치명적 오류가 있어 배포할 수 없습니다.'); return }
    if (config.maintenanceMode.enabled && !window.confirm('점검 모드를 켠 채로 배포하면 모든 사용자가 즉시 앱을 사용할 수 없게 됩니다. 계속할까요?')) return
    setBusy(true); setMessage(''); setError('')
    try {
      const version = await publishCmsContent('featureSettings', config, { uid: auth.user.uid })
      await load()
      setMessage(`v${version}으로 배포했습니다.`)
    } catch (err) { setError(err.validation ? '치명적 오류가 있어 배포할 수 없습니다.' : err.message) } finally { setBusy(false) }
  }
  async function rollback(version) {
    if (!window.confirm(`v${version}으로 롤백할까요?`)) return
    setBusy(true); setMessage(''); setError('')
    try { const next = await rollbackCmsContent('featureSettings', version, { uid: auth.user.uid }); await load(); setMessage(`v${version} → v${next}로 롤백했습니다.`) } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  function addToggle() {
    const key = newToggleKey.trim()
    if (!key || key in config.featureToggles) return
    setConfig(c => ({ ...c, featureToggles: { ...c.featureToggles, [key]: true } }))
    setNewToggleKey('')
  }
  function removeToggle(key) {
    setConfig(c => { const next = { ...c.featureToggles }; delete next[key]; return { ...c, featureToggles: next } })
  }

  if (loading) return <div className="empty-state">기능 설정을 불러오는 중…</div>
  return (
    <>
      <header className="page-header"><div><p className="eyebrow">시스템</p><h1>서비스 설정</h1><p>서비스 상태, 기능 활성화, 최소 지원 버전과 업데이트 안내를 관리합니다. 배포하면 즉시 반영됩니다.</p></div></header>

      {error && <div className="error-state">{error}</div>}
      {message && <div className="temporary-warning">{message}</div>}
      {config.maintenanceMode.enabled && <div className="conflict-warning"><AlertTriangle size={14} style={{ verticalAlign: -2 }} /> 점검 모드가 켜져 있습니다 — 배포하면 모든 사용자가 앱을 사용할 수 없습니다.</div>}

      <section className="panel" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12 }}>
            <span className={`status-badge ${dirty ? 'new' : 'published'}`}>{dirty ? '미저장 변경 있음' : '변경 없음'}</span>
            <span className="muted">production v{production?.version ?? 0}{draft ? ` · 초안 저장 ${formatDateTime(draft.savedAt)}` : ' · 초안 없음'}</span>
            <span className="muted">production 대비 변경: {changedFields.length > 0 ? changedFields.join(', ') : '없음'}</span>
            {validation.errors.length > 0 && <span className="status-badge ended"><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> 오류 {validation.errors.length}</span>}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {draft && <button className="button secondary" type="button" disabled={busy} onClick={discardDraft}><RotateCcw size={16} />초안 버리기</button>}
              <button className="button secondary" type="button" disabled={busy || !dirty} onClick={saveDraft}><Save size={16} />초안 저장</button>
              <button className="button primary" type="button" disabled={busy || !validation.valid} onClick={publish}><Send size={16} />검증 후 배포</button>
              {versions.length > 0 && (
                <select className="select" style={{ width: 200 }} disabled={busy} value="" onChange={e => e.target.value && rollback(Number(e.target.value))}>
                  <option value="">이전 버전으로 롤백…</option>
                  {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {formatDateTime(v.publishedAt)}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
        {validation.errors.length > 0 && (
          <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--danger)' }}>
            {validation.errors.map((entry, idx) => <li key={idx}>{entry.field}: {entry.message}</li>)}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="form-section"><h2>점검 모드</h2>
          <label className="check-chip" style={{ marginBottom: 12 }}><input type="checkbox" disabled={!canEdit} checked={config.maintenanceMode.enabled} onChange={e => setConfig(c => ({ ...c, maintenanceMode: { ...c.maintenanceMode, enabled: e.target.checked } }))} />점검 모드 사용(켜면 앱 전체가 차단됩니다)</label>
          <textarea className="textarea" disabled={!canEdit || !config.maintenanceMode.enabled} placeholder="예: 서버 점검으로 오전 2시~4시 서비스가 중단됩니다." value={config.maintenanceMode.message} onChange={e => setConfig(c => ({ ...c, maintenanceMode: { ...c.maintenanceMode, message: e.target.value } }))} />
        </div>

        <div className="form-section"><h2>운영 메시지</h2>
          <label className="check-chip" style={{ marginBottom: 12 }}><input type="checkbox" disabled={!canEdit} checked={config.operationalMessage.enabled} onChange={e => setConfig(c => ({ ...c, operationalMessage: { ...c.operationalMessage, enabled: e.target.checked } }))} />운영 메시지 사용</label>
          <textarea className="textarea" disabled={!canEdit || !config.operationalMessage.enabled} placeholder="예: 새 학기 시간표 등록 기간 안내" value={config.operationalMessage.message} onChange={e => setConfig(c => ({ ...c, operationalMessage: { ...c.operationalMessage, message: e.target.value } }))} />
        </div>

        <div className="form-section"><h2>최소 버전 · 강제 업데이트</h2><div className="field-grid">
          <div className="field"><label>iOS 최소 버전</label><input className="input" disabled={!canEdit} value={config.minVersion.ios} onChange={e => setConfig(c => ({ ...c, minVersion: { ...c.minVersion, ios: e.target.value } }))} placeholder="예: 1.2.0" /></div>
          <div className="field"><label>Android 최소 버전</label><input className="input" disabled={!canEdit} value={config.minVersion.android} onChange={e => setConfig(c => ({ ...c, minVersion: { ...c.minVersion, android: e.target.value } }))} placeholder="예: 1.2.0" /></div>
          <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={config.forceUpdate.ios} onChange={e => setConfig(c => ({ ...c, forceUpdate: { ...c.forceUpdate, ios: e.target.checked } }))} />iOS 강제 업데이트</label></div>
          <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={config.forceUpdate.android} onChange={e => setConfig(c => ({ ...c, forceUpdate: { ...c.forceUpdate, android: e.target.checked } }))} />Android 강제 업데이트</label></div>
          <div className="field full"><label>강제 업데이트 안내 메시지</label><input className="input" disabled={!canEdit} value={config.forceUpdate.message} onChange={e => setConfig(c => ({ ...c, forceUpdate: { ...c.forceUpdate, message: e.target.value } }))} placeholder="예: 새 버전으로 업데이트해 주세요." /></div>
        </div>
          <p className="field-help">web은 앱스토어 업데이트 개념이 없어 강제 업데이트 대상에서 제외됩니다.</p>
        </div>

        <div className="form-section"><h2>기능 토글</h2>
          {Object.keys(config.featureToggles).length === 0 && <p className="field-help" style={{ marginBottom: 10 }}>등록된 기능 토글이 없습니다.</p>}
          {Object.entries(config.featureToggles).map(([key, value]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
              <label className="check-chip" style={{ flex: 1 }}><input type="checkbox" disabled={!canEdit} checked={value} onChange={e => setConfig(c => ({ ...c, featureToggles: { ...c.featureToggles, [key]: e.target.checked } }))} />{key}</label>
              {canEdit && <button type="button" className="icon-button" onClick={() => removeToggle(key)}><Trash2 size={14} /></button>}
            </div>
          ))}
          {canEdit && (
            <div className="toolbar" style={{ marginTop: 10 }}>
              <input className="input" placeholder="새 기능 키 (예: chatbot_beta)" value={newToggleKey} onChange={e => setNewToggleKey(e.target.value)} />
              <button type="button" className="button secondary" onClick={addToggle}><Plus size={14} />추가</button>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
